import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { DocumentEditModal, API_MODULE_DOCUMENT } from "@l2r-front/l2r-app-doc"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { NoteAddRoundedIcon } from "@l2r-front/l2r-icons"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useIsModuleAvailableForItem, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { BottomSheetContent, MoreMenu, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { RiskZoneAddDocumentForm } from "../../forms/RiskZoneAddDocumentForm"
import { useRiskZoneCluster } from "../../hooks/queries/useRiskZoneClusters"

import { RiskZoneDocumentList } from "../RiskZoneDocumentList"
import { RiskZonesGrades } from "../RiskZonesGrades"
import { RiskZonesTrafficInfo } from "../RiskZonesTrafficInfo"

import * as Styled from "./RiskZonesDetailSidebar.styled"

export function RiskZonesDetailSidebar() {

    const [documentAddModalOpen, setDocumentAddModalOpen] = useState(false)
    const { riskZoneId, road } = useParams()

    const { data: riskZoneCluster } = useRiskZoneCluster(riskZoneId, {}, { enabled: !!riskZoneId })

    const { t } = useTranslation(I18N_NAMESPACE)
    const navigate = useNavigateWithSearchParams()
    const { setMapPin } = useMapDispatchContext()

    const subNetworksFromClusterSet = useMemo(() => {
        return (riskZoneCluster || []).reduce((acc, curr) => {
            if (curr.sub_networks && curr.sub_networks.length) {
                curr.sub_networks.forEach(sn => acc.add(sn))
            }
            return acc
        }, new Set())
    }, [riskZoneCluster])

    const documentAvailable = useIsModuleAvailableForItem(Array.from(subNetworksFromClusterSet), API_MODULE_DOCUMENT)

    const readOnly = useRoleIsReadOnly(Array.from(subNetworksFromClusterSet), API_MODULE_DOCUMENT)
    const isMobile = useIsMobileDevice()

    const navigateBack = useCallback(() => {
        setMapPin()
        navigate(`../${road}`, { navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL })
    }, [navigate, road, setMapPin])

    return (
        <BottomSheetContent
            id="riskzones-sidebar"
            onBack={road ? navigateBack : null}
            title={t(I18N_NAMESPACE, "containers.riskZonesDetailSidebar.title", { id: riskZoneId })}
            titleSideComponent={
                <MoreMenu
                    menuConfig={[
                        {
                            displayed: documentAvailable && !readOnly,
                            id: "add-document",
                            label: t(I18N_NAMESPACE, "containers.riskZonesDetailSidebar.addDocument"),
                            icon: <NoteAddRoundedIcon color="common/black_states" />,
                            props: {
                                onClick: () => setDocumentAddModalOpen(true),
                            },
                        },
                    ]} />}
        >
            <Styled.Container>
                <Styled.ContentWrapper>
                    <Styled.RoadDetailInfo isMobile={isMobile} road={road} />
                    <RiskZonesGrades />
                    <RiskZonesTrafficInfo />
                    <RiskZoneDocumentList riskZoneId={riskZoneId} />
                </Styled.ContentWrapper>
            </Styled.Container>
            {documentAddModalOpen && <DocumentEditModal
                open={documentAddModalOpen}
                onClose={() => setDocumentAddModalOpen(false)}
                FormComponent={RiskZoneAddDocumentForm}
                riskZoneId={riskZoneId}
            />}
        </BottomSheetContent>
    )
}